<template>
  <div
    class="layer"
    :class="{ expanded }"
  >
    <div
      class="layer-header"
      :class="{'disabled': usageThresholdReached}"
      :data-testid="layer.id + '-header'"
      @click="handleHeaderClick()"
    >
      <span>
        <label
          v-if="usageThresholdReached"
          class="usage-warning"
          @click.stop
        >
          <v-icon
            size="small"
            color="warning"
            variant="text"
            icon="mdi-alert"
            style="margin-right: 7px;"
          />
          <span class="tooltip">
            <p>The usage for this layer has exceeded the monthly limit</p>
          </span>
        </label>
        <label
          v-else
          class="toggle-item"
          :data-testId="layer.id + '-label'"
          @click.stop
        >
          <input
            :id="layer.id"
            :disabled="layer.loading"
            type="checkbox"
            :data-testid="layer.id + '-checkbox'"
            :checked="layer.checked"
            @click="$emit('toggle-checked')"
          >
          <span class="checkmark" />
        </label>
        <h3 :class="['content-title layer-header-name', { disabled: usageThresholdReached }]">{{ layer.name }}</h3>
        <div
          v-if="layer.tooltip"
          class="info-icon"
        >
          <v-tooltip
            location="bottom start"
            :text="layer.tooltip"
            open-delay="200"
            width="250"
          >
            <template #activator="{ props }">
              <TooltipInfoIcon v-bind="props" />
            </template>
          </v-tooltip>
        </div>
        <span
          v-show="layer.checked && (hasHiddenSublayers || layer.hasActiveFilters)"
          class="pl-1"
          style="color: forestgreen"
          data-testid="green-dot"
        >●
        </span>
      </span>
      <CaretIcon
        v-if="hasContent(layer)"
        :id="layer.id + '-caret'"
        class="caret"
      />
    </div>
    <div
      v-if="shouldDisplayBody(layer)"
      class="layer-body"
      :data-testid="layer.id + '-body'"
    >
      <LoadIcon
        v-show="layer.loading"
        class="load-icon"
      />
      <p
        v-show="showZoomOut||showZoomIn"
        class="zoom-hint"
      >
        Zoom {{ showZoomIn ? 'in' : 'out' }} to see layer
      </p>
      <div v-if="zoomOk && !layer.loading">
        <div
          v-if="layer.type === 'wmtsTile'"
          class="zoom-hint"
        >
          Click the lines to see providers
        </div>
        <FillOpacitySlider
          v-if="showFillOpacity"
          :model-value="fillOpacityPercent"
          @update:model-value="layer.setFillOpacity(toOpacity($event))"
          @commit="$emit('save-fill-opacity', toOpacity($event))"
        />
        <!-- If the legend is vertical, radio buttons should be above -->
        <LegendRadioButtons
          v-if="showRadioButtons && layer.legendStyle === 'vertical'"
          :options="validOptions"
          class="pb-2"
          @selected-option="$emit('selected-option', $event)"
        />
        <slot name="legendBody" />
        <!-- If the legend is horizontal, radio buttons should be below -->
        <LegendRadioButtons
          v-if="showRadioButtons && layer.legendStyle === 'horizontal'"
          :options="validOptions"
          @selected-option="$emit('selected-option', $event)"
        />
        <slot name="extraData" />
      </div>
    </div>
    <div
      v-else-if="layer.type === 'vectorTile' && layer.loading && layer.legendStyle !== 'hidden'"
    >
      <LoadIcon
        class="load-icon"
      />
    </div>
  </div>
</template>

<script>
import CaretIcon from '@/components/svgs/CaretIcon.vue';
import LoadIcon from '@/components/svgs/LoadIcon.vue';
import TooltipInfoIcon from '@/components/svgs/TooltipInfoIcon.vue';
import LegendRadioButtons from './Legend/LegendRadioButtons.vue';
import FillOpacitySlider from './FillOpacitySlider.vue';

export default {
  name: 'LayerOption',
  components: {
    CaretIcon,
    LoadIcon,
    TooltipInfoIcon,
    LegendRadioButtons,
    FillOpacitySlider,
  },
  props: {
    layer: {type: Object, required: true},
    currentZoom: {type: Number, required: true},
    totalUsage: {type: Number, required: false, default: null},
  },
  emits: [
    'toggle-checked',
    'toggle-expanded',
    'selected-option',
    'save-fill-opacity',
  ],
  computed: {
    validOptions() {
      // Explicitly depend on currentZoom to ensure reactivity for the layer's options
      return this.currentZoom !== undefined ? this.layer.validOptions : [];
    },
    /**
     * @return {boolean} returns true if the current zoom level is within the min/max limits for this layer
     */
    zoomOk() {
      return this.currentZoom >= this.layer.minZoom && this.currentZoom <= this.layer.maxZoom;
    },
    showLegend() {
      return !this.layer.loading && this.zoomOk && this.layer.labels.length && this.layer.colors.length;
    },
    showRadioButtons() {
      return this.validOptions?.length > 1 && !this.layer.loading && this.zoomOk;
    },
    showFillOpacity() {
      return this.layer.fillOpacityEnabled && this.layer.type === 'vectorTile';
    },
    fillOpacityPercent() {
      return this.layer.fillOpacity === undefined ? 100 : Math.round(this.layer.fillOpacity * 100);
    },
    showZoomIn() {
      return this.currentZoom < this.layer.minZoom && !this.layer.loading;
    },
    showZoomOut() {
      return this.currentZoom > this.layer.maxZoom && !this.layer.loading;
    },
    expanded() {
      return (this.layer.expanded || ((this.layer.id === 'parcels' || this.layer.id === 'places') && this.layer.checked));
    },
    usageThresholdReached() {
      return this.layer?.totalUsageThreshold && this.totalUsage && (this.layer.totalUsageThreshold <= this.totalUsage);
    },
    hasHiddenSublayers() {
      return this.layer?.activeLegendGroups?.some((group) => group.hasInvisibleItems());
    },
  },
  unmounted() {
    // If the layer is removed from the map while displaying, hide it
    if (this.layer.checked) {
      this.$emit('toggle-checked', true);
    }
  },
  methods: {
    /**
     * 100% leaves the stylesheet's own alpha alone, which the layer stores as no value at all rather than as 1.
     */
    toOpacity(percent) {
      return percent === 100 ? undefined : percent / 100;
    },
    /**
     * @param {Object} layer the layer to check the properties of to see if it is has content
     * @return {boolean} true if layer has content, false if it is not
     */
    hasContent(layer) {
      // Disable the layer if the usage threshold has been reached
      if (this.usageThresholdReached) return false;
      // If the layer is not visible, it should not have content
      if (layer.legendStyle === 'hidden') return false;
      // If there are multiple options the layer should be expandable
      const hasMultipleOptions = layer.options.length > 1;
      // If there are labels and colors the layer should be expandable
      const hasLabelsAndColors = layer.labels?.length && layer.colors?.length;
      // If legendItems exist, the layer should be expandable
      const hasLegendItems = layer.legendItems?.length || layer.hasLegend;
      // If the layer is for drone imagery
      const isDroneLayer = layer.id === 'drone';
      const isFiberLayer = layer.id === 'metro' || layer.id === 'longhaul';

      // Return whether any of the above criteria are true
      return hasMultipleOptions || hasLabelsAndColors || hasLegendItems || isDroneLayer || isFiberLayer ||
        layer.hasFilters || this.showFillOpacity;
    },
    /**
     * @param {Object} layer the layer to check the properties of to see if the body should display
     * @return {boolean} true if the body should be displaying, false if it should not
     */
    shouldDisplayBody(layer) {
      // If the layer is not expanded, body should not be displayed
      if (!this.expanded) return false;
      // If the layer is not visible, it should not have a body
      if (layer.legendStyle === 'hidden') return false;
      // If the layer is expanded and either:
      // - should show zoom in/zoom out body or
      // - has layer content
      // Then the body should be displayed.
      return this.showZoomIn || this.showZoomOut || this.hasContent(layer);
    },
    handleHeaderClick() {
      if (this.usageThresholdReached) return;
      this.$emit('toggle-expanded');
    },
  },
};
</script>

<style scoped lang="scss">
.load-icon {
  margin: auto;
}
.zoom-hint {
  font-size: 14px;
  text-align: center;
}
.layer {
  border: 1px solid main.$lightGray;
  box-shadow: #0005 1px 1px 3px 0;
  border-radius: 4px;
  margin-top: 0.5rem;

  &-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: main.$lightGray;
    font-weight: bold;
    padding: 1.2rem 0.5rem;
    font-size: 12px;
    height: 25px;
    cursor: pointer;
    &.disabled {
      cursor: default;
    }
    &-name {
      letter-spacing: 0px;
      &.disabled {
        opacity: 0.6;
      }
    }
    input[type="checkbox"] {
      margin-right: 5px;
    }
    &>span {
      display: flex;
      align-items: center;
    }
    .info-icon {
      &:hover {
        .tooltip {
          visibility: visible;
          opacity: 1;
          transform: translate(0%, 30%);
          text-align: left;
          text-transform: none;
        }
      }
      color: main.$npGray;
      font-size: 8px;
      text-align: left;
      font-family: main.$fontPrimary;
      width: 18px;
      height: 18px;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      cursor: default;
    }
    .usage-warning {
      &:hover {
        .tooltip {
          visibility: visible;
          opacity: 1;
          text-align: left;
          text-transform: none;
        }
      }
    }
    .caret {
      height: 40px;
      width: 20px;
    }
  }
  &.expanded {
    .caret {
      transform: rotate(180deg) scale(1.5);
    }
  }
  &-body {
    padding: 0.75rem;
  }
  @include main.respond(desktop) {
      &-header {
          font-size: 14px;
      }
  }
}

/*
    Custom checkbox css
    (Hide the default checkbox and render a custom one)
*/

.toggle-item {
  display: block;
  position: relative;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  margin-right: 1.5rem;
  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
    // CSS specific to after <input> is clicked
    &:checked~.checkmark {
      background-color: main.$npOrange;
      border: none;
      &:after {
        display: block;
      }
    }
  }
  .checkmark {
    position: absolute;
    top: -8px;
    left: 0px;
    height: 16px;
    width: 16px;
    border-radius: 2px;
    border: 2px solid main.$npGray;
    &:after {
      content: "";
      position: absolute;
      display: none;
      left: 5px;
      top: 1px;
      width: 6px;
      height: 10px;
      border: solid white;
      border-width: 0 2px 2px 0;
      -webkit-transform: rotate(45deg);
      -ms-transform: rotate(45deg);
      transform: rotate(45deg);
    }
  }
}
</style>
